import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Phone, Pencil, Trash2 } from "lucide-react";
import { PhoneNumber } from "@/hooks/use-phone-numbers";
import ConfigureWebhookButton from "./ConfigureWebhookButton";

/** null only when nothing is assigned; a routed number whose name has not
 * arrived reads as "Agent assigned" rather than as an error. */
function agentLabelFor(agentId: string | null, agentName: string | null): string | null {
  if (!agentId) return null;
  return agentName ?? "Agent assigned";
}

interface PhoneNumberCardProps {
  phoneNumber: PhoneNumber;
  onEdit: (phoneNumber: PhoneNumber) => void;
  onDelete: (phoneNumber: PhoneNumber) => void;
  canEdit: boolean;
  canDelete: boolean;
}

export default function PhoneNumberCard({ phoneNumber, onEdit, onDelete, canEdit, canDelete }: PhoneNumberCardProps) {
  // Whether a number is routed is the id, never the name: the name is null for
  // an agent outside the number's workspace, and reading "no name" as "no
  // agent" puts a red "No agent assigned" on a number that is in fact wired up.
  const inboundAgent = agentLabelFor(phoneNumber.inbound_agent_id, phoneNumber.inbound_agent_name);
  const outboundAgent = agentLabelFor(
    phoneNumber.outbound_agent_id,
    phoneNumber.outbound_agent_name,
  );

  return (
    <Card className="p-6" data-testid="phone-card" data-phone-number={phoneNumber.phone_number}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1">
          <div className="p-3 rounded-lg bg-primary/10">
            <Phone className="w-5 h-5 text-primary" />
          </div>
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-lg">{phoneNumber.phone_number}</h3>
              {phoneNumber.provider === "livekit_sip" ? (
                <Badge variant="outline" className="text-purple-600 border-purple-600">
                  LiveKit SIP
                </Badge>
              ) : (
                <Badge variant="outline" className="text-blue-600 border-blue-600">
                  Twilio
                </Badge>
              )}
              <Badge variant="outline" className="text-muted-foreground">
                {phoneNumber.max_cps} CPS
              </Badge>
            </div>
            {phoneNumber.nickname && (
              <p className="text-sm text-muted-foreground">{phoneNumber.nickname}</p>
            )}
            <div className="flex flex-col gap-2 mt-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-blue-600 border-blue-600">
                  Inbound
                </Badge>
                {inboundAgent ? (
                  <span className="text-sm">{inboundAgent}</span>
                ) : (
                  <span className="text-sm text-destructive">No agent assigned</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-green-600 border-green-600">
                  Outbound
                </Badge>
                {outboundAgent ? (
                  <span className="text-sm">{outboundAgent}</span>
                ) : (
                  <span className="text-sm text-destructive">No agent assigned</span>
                )}
              </div>
              {/* Only a SIP-trunk number calls it; a Twilio number's stored URL is legacy and never fires. */}
              {phoneNumber.provider === "livekit_sip" && phoneNumber.inbound_webhook_url && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-purple-600 border-purple-600">
                    Inbound webhook
                  </Badge>
                  <span className="text-xs text-muted-foreground font-mono truncate">
                    {phoneNumber.inbound_webhook_url}
                  </span>
                </div>
              )}
            </div>
            {phoneNumber.provider === "twilio" && canEdit && (
              <div className="mt-4">
                <ConfigureWebhookButton phoneNumber={phoneNumber} />
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(phoneNumber)}
              aria-label={`Edit ${phoneNumber.phone_number}`}
              data-testid="phone-edit"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(phoneNumber)}
              aria-label={`Delete ${phoneNumber.phone_number}`}
              data-testid="phone-delete"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
